/**
 * Minimal modal <Dialog> — hand-roll (Phase 2, #mc-phase2).
 *
 * NOT shadcn's Radix-backed Dialog (no `@radix-ui/react-dialog` dep,
 * and Phase 2 says don't add deps). The pattern mirrors the bespoke
 * slide-out in `task-detail-panel.tsx`: a fixed-inset overlay, a
 * backdrop that closes on click, Escape-to-close, `role="dialog"` +
 * `aria-modal`, and a small focus trap so a keyboard user can't tab
 * out of the modal while it's open.
 *
 * The trap reuses the same approach as `task-detail-panel.tsx`:
 *   - On open, focus the first focusable inside (or the panel itself).
 *   - Tab at the last focusable wraps to the first; Shift+Tab at the
 *     first wraps to the last.
 *   - On close, restore focus to whatever had it when the dialog
 *     opened.
 *
 * Composition: <Dialog open onClose>…</Dialog> renders the overlay +
 * panel and the children inside the panel. `<DialogHeader>`,
 * `<DialogTitle>`, `<DialogDescription>`, `<DialogFooter>` are thin
 * layout helpers so callers don't re-derive the spacing each time.
 */

import * as React from "react";
import { cn } from "@/lib/utils";

const FOCUSABLE_SELECTOR = [
  "button:not([disabled])",
  "textarea:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  "a[href]",
  "[tabindex]:not([tabindex='-1'])",
].join(", ");

export function Dialog({
  open,
  onClose,
  children,
  className,
  labelledBy,
  describedBy,
  testId = "dialog",
}: {
  open: boolean;
  onClose: () => void;
  children: React.ReactNode;
  className?: string;
  /** id of the element that titles the dialog (wired to aria-labelledby). */
  labelledBy?: string;
  /** id of the element that describes the dialog (wired to aria-describedby). */
  describedBy?: string;
  testId?: string;
}) {
  const panelRef = React.useRef<HTMLDivElement | null>(null);
  const triggerRef = React.useRef<Element | null>(null);

  // Escape-to-close — only while open.
  React.useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // Focus trap — runs once per open. Cleanup restores focus to trigger.
  React.useEffect(() => {
    if (!open) return;
    const panel = panelRef.current;
    if (!panel) return;

    triggerRef.current = document.activeElement;

    const initial =
      panel.querySelector<HTMLElement>(FOCUSABLE_SELECTOR) ?? panel;
    initial.focus();

    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Tab") return;
      const focusables = Array.from(
        panel.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
      ).filter((el) => !el.hasAttribute("aria-hidden"));
      if (focusables.length === 0) {
        e.preventDefault();
        return;
      }
      const first = focusables[0]!;
      const last = focusables[focusables.length - 1]!;
      const active = document.activeElement;
      if (e.shiftKey) {
        if (active === first || !panel.contains(active)) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (active === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      const trigger = triggerRef.current;
      if (trigger instanceof HTMLElement && document.body.contains(trigger)) {
        trigger.focus();
      }
    };
  }, [open]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50" data-testid={`${testId}-root`}>
      <button
        type="button"
        aria-label="Close dialog"
        onClick={onClose}
        className="absolute inset-0 cursor-default bg-black/40"
        data-testid={`${testId}-backdrop`}
      />
      <div className="absolute inset-0 flex items-start justify-center overflow-y-auto p-4 sm:p-8">
        <div
          ref={panelRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby={labelledBy}
          aria-describedby={describedBy}
          tabIndex={-1}
          data-testid={testId}
          className={cn(
            "relative mt-8 w-full max-w-lg rounded-xl border bg-card text-card-foreground p-6 shadow-xl",
            className,
          )}
        >
          {children}
        </div>
      </div>
    </div>
  );
}

export function DialogHeader({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn("mb-4 flex flex-col gap-1.5", className)}
      {...props}
    />
  );
}

export function DialogTitle({
  className,
  ...props
}: React.HTMLAttributes<HTMLHeadingElement>) {
  return (
    <h2
      className={cn(
        "text-lg font-sans font-semibold leading-none tracking-tight",
        className,
      )}
      {...props}
    />
  );
}

export function DialogDescription({
  className,
  ...props
}: React.HTMLAttributes<HTMLParagraphElement>) {
  return (
    <p className={cn("text-sm text-muted-foreground", className)} {...props} />
  );
}

export function DialogFooter({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        "mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end",
        className,
      )}
      {...props}
    />
  );
}
